import { insertObjectIf } from '@/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TablePaginationConfig } from 'antd';

import { ColumnTypes } from '../../table/type';
import {
	ExpandableTableConfig,
	ExpandableTableProps,
	ExpandableTableState,
	GroupedData,
	UseExpandableTableReturn,
} from '../types';

export const useExpandableTable = <
	T extends Record<string, any> = Record<string, any>,
>(
	dataSource: T[],
	columns: ColumnTypes,
	config: ExpandableTableConfig<T>,
	autoExpandAll: boolean = false,
	tableProps?: ExpandableTableProps<T>['tableProps']
): UseExpandableTableReturn<T> => {
	const { rowSelection, pagination } = tableProps || {};

	const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

	// Group data theo config
	const groupedData: GroupedData<T>[] = useMemo(() => {
		const groups = dataSource.reduce(
			(acc, item) => {
				const rowKey = String(item[config.rowKey]);
				const groupKey = String(item[config.groupBy]);
				const groupTitle = String(item[config.groupTitleKey]);

				if (!acc[groupKey]) {
					acc[groupKey] = {
						rowKey,
						groupKey,
						groupTitle,
						items: [],
						count: 0,
						expanded: false,
					};
				}

				// Tính file count
				if (config.countFunction) {
					acc[groupKey].count = config.countFunction(acc[groupKey].items);
				} else {
					acc[groupKey].items.push(item);
					acc[groupKey].count++;
				}

				return acc;
			},
			{} as Record<string, GroupedData<T>>
		);

		return Object.values(groups);
	}, [dataSource, config]);

	// Transform data để hiển thị
	const transformedDataSource = useMemo(() => {
		return groupedData.flatMap((group) => {
			const groupHeader = {
				...group.items[0], // Lấy item đầu tiên làm base
				[config.rowKey]: `group-${group.groupKey}`,
				[config.groupBy]: group.groupKey,
				[config.groupTitleKey]: group.groupTitle,
				isGroupHeader: true,
				expanded: expandedGroups.has(group.groupKey),
				groupCount: group.count,
			} as T & {
				isGroupHeader: boolean;
				expanded: boolean;
				groupCount: number;
			};

			const groupItems = expandedGroups.has(group.groupKey)
				? group.items.map((item) => ({
						...item,
						isGroupChild: true,
					}))
				: [];

			return [groupHeader, ...groupItems];
		});
	}, [groupedData, expandedGroups, config]);

	// Toggle một group
	const toggleGroup = useCallback((groupKey: string) => {
		setExpandedGroups((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(groupKey)) {
				newSet.delete(groupKey);
			} else {
				newSet.add(groupKey);
			}
			return newSet;
		});
	}, []);

	// Expand tất cả
	const expandAll = useCallback(() => {
		setExpandedGroups(new Set(groupedData.map((group) => group.groupKey)));
	}, [groupedData]);

	// Collapse tất cả
	const collapseAll = useCallback(() => {
		setExpandedGroups(new Set());
	}, []);

	// Toggle tất cả
	const toggleAll = useCallback(() => {
		if (expandedGroups.size === groupedData.length) {
			collapseAll();
		} else {
			expandAll();
		}
	}, [expandedGroups.size, groupedData.length, expandAll, collapseAll]);

	// Check group có expanded không
	const isGroupExpanded = useCallback(
		(groupKey: string) => {
			return expandedGroups.has(groupKey);
		},
		[expandedGroups]
	);

	// State hiện tại
	const state: ExpandableTableState = useMemo(
		() => ({
			expandedGroups,
			allExpanded: expandedGroups.size === groupedData.length,
			allCollapsed: expandedGroups.size === 0,
		}),
		[expandedGroups, groupedData.length]
	);

	// Selection helpers
	// Lookup maps
	const documentTypeToChildIds = useMemo(() => {
		const mapDoc = new Map<string, React.Key[]>();
		groupedData.forEach((group) => {
			mapDoc.set(
				group.groupKey,
				group.items.map((d) => d[config.rowKey] as unknown as React.Key)
			);
		});
		return mapDoc;
	}, [groupedData]);

	const idToRow = useMemo(() => {
		const m = new Map<React.Key, T>();
		groupedData.forEach((group) => {
			group.items.forEach((d) => {
				m.set(d[config.rowKey] as unknown as React.Key, d);
			});
		});
		return m;
	}, [groupedData]);

	// Row selection grouped by documentType: selecting one item selects all items of the same documentType
	const groupedRowSelection =
		rowSelection &&
		({
			...rowSelection,
			onChange: (newSelectedRowKeys: React.Key[]) => {
				const keys = new Set<React.Key>(newSelectedRowKeys || []);

				// 0) If a header was unchecked in this change, remove all its children first
				const isHeaderKey = (k: React.Key) =>
					typeof k === 'string' && (k as string).startsWith('group-');
				const headerToDocType = (k: React.Key) =>
					(k as string).replace('group-', '');
				const prevHeaderDocTypes = new Set<string>(
					((rowSelection?.selectedRowKeys || []) as React.Key[])
						.filter(isHeaderKey)
						.map(headerToDocType)
				);
				const nextHeaderDocTypes = new Set<string>(
					Array.from(keys).filter(isHeaderKey).map(headerToDocType)
				);
				Array.from(prevHeaderDocTypes)
					.filter((d) => !nextHeaderDocTypes.has(d))
					.forEach((docType) => {
						(documentTypeToChildIds.get(docType) || []).forEach((id) =>
							keys.delete(id)
						);
					});

				// 1) Expand ONLY headers that were newly checked to include all children
				const addedHeaders: string[] = Array.from(nextHeaderDocTypes).filter(
					(d) => !prevHeaderDocTypes.has(d)
				);
				addedHeaders.forEach((docType) => {
					(documentTypeToChildIds.get(docType) || []).forEach((id) =>
						keys.add(id)
					);
				});

				// 2) After expanding (and handling removed headers), set header checked only when all children selected
				documentTypeToChildIds.forEach((childIds, docType) => {
					const allSelected = childIds.every((id) => keys.has(id));
					const headerKey = `group-${docType}` as unknown as React.Key;
					if (allSelected) keys.add(headerKey);
					else keys.delete(headerKey);
				});

				// 3) Build final rows from child keys only
				const finalRows: T[] = Array.from(keys)
					.filter(
						(k) => typeof k !== 'string' || !(k as string).startsWith('group-')
					)
					.map((k) => idToRow.get(k)!)
					.filter(Boolean);

				rowSelection?.setState?.({
					selectedRowKeys: Array.from(keys),
					selectedRows: finalRows,
				});
			},
		} as typeof rowSelection);

	const transformedPagination = useMemo(() => {
		const groupHeaderCount = groupedData.length;

		const total = Number((pagination as TablePaginationConfig)?.total || 0);

		const pageSize = Number(
			(pagination as TablePaginationConfig)?.pageSize || 0
		);

		const defaultPageSize = Number(
			(pagination as TablePaginationConfig)?.defaultPageSize || 0
		);

		return {
			...pagination,
			...insertObjectIf(pagination, {
				total: total + groupHeaderCount * Math.ceil(total / pageSize),
				totalLocal: total,
				pageSize: pageSize + groupHeaderCount,
				pageSizeLocal: pageSize,
				defaultPageSize: defaultPageSize + groupHeaderCount,
				defaultPageSizeLocal: defaultPageSize,
			}),
		};
	}, [pagination, groupedData.length]);

	// Auto expand all khi load
	useMemo(() => {
		if (autoExpandAll) {
			expandAll();
		}
	}, [autoExpandAll, expandAll]);

	const isInitializedRef = useRef(false);
	useEffect(() => {
		if (groupedData.length > 0 && !isInitializedRef.current) {
			expandAll();
			isInitializedRef.current = true;
		}
	}, [groupedData]);

	return {
		state,
		transformedDataSource,
		transformedColumns: columns,
		transformedPagination,
		groupedData,
		groupedRowSelection,
		toggleGroup,
		expandAll,
		collapseAll,
		toggleAll,
		isGroupExpanded,
	};
};
